import React, { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import styles from './ProjectFormModal.module.css';

const ProjectFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitState, setSubmitState] = useState('idle'); // idle | sending | sent | error
  const overlayRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    // Focus first input
    setTimeout(() => firstInputRef.current?.focus(), 100);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitState('sending');

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitState('sent');
        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
        setTimeout(() => {
          setSubmitState('idle');
          onClose();
        }, 2500);
      } else {
        setSubmitState('error');
        setTimeout(() => setSubmitState('idle'), 3000);
      }
    } catch {
      setSubmitState('error');
      setTimeout(() => setSubmitState('idle'), 3000);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Start your project"
    >
      <div className={styles.modal}>
        <button className={styles.close} onClick={onClose} aria-label="Close dialog">
          <X size={20} />
        </button>

        <h2 className={styles.title}>Start Your Project</h2>
        <p className={styles.subtitle}>Tell us about your requirements and we'll get back to you soon</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <input
              ref={firstInputRef}
              type="text"
              name="name"
              placeholder="Your Name *"
              value={formData.name}
              onChange={handleChange}
              required
              className={styles.input}
            />
            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={handleChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.row}>
            <input
              type="text"
              name="company"
              placeholder="Company Name"
              value={formData.company}
              onChange={handleChange}
              className={styles.input}
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="">Select Service *</option>
            <option value="Agentic AI Systems">Agentic AI Systems</option>
            <option value="Generative AI & RAG">Generative AI &amp; RAG</option>
            <option value="Business Process Transformation">Business Process Transformation</option>
            <option value="SaaS & Platform Implementation">SaaS &amp; Platform Implementation</option>
            <option value="Cloud Engineering">Cloud Engineering</option>
            <option value="Data Engineering">Data Engineering</option>
            <option value="Security & Compliance">Security &amp; Compliance</option>
            <option value="Staff Augmentation">Staff Augmentation</option>
            <option value="Other">Other</option>
          </select>

          <textarea
            name="message"
            placeholder="Describe your project requirements..."
            value={formData.message}
            onChange={handleChange}
            rows="4"
            className={styles.textarea}
            required
          />

          <button type="submit" className={styles.submit} disabled={submitState === 'sending' || submitState === 'sent'}>
            {submitState === 'idle' && <><Send size={18} /> Send Requirements</>}
            {submitState === 'sending' && 'Sending...'}
            {submitState === 'sent' && 'Sent! We\'ll be in touch.'}
            {submitState === 'error' && 'Failed — please try again'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProjectFormModal;
